import type { FichaTexts, HistoryField, HistoryTexts, LineupTexts, Media, NewsField, NewsLang, NewsTexts } from '../../api/types';
import { perLang, tk, useT } from '../../i18n';

/** Idiomas del sitio de Phelon & Moore, en el orden de su selector (el nombre se traduce con t() al mostrarlo). */
export const LANGS: { code: NewsLang; label: string; name: string }[] = [
  { code: 'en', label: 'EN', name: tk('Inglés') },
  { code: 'es', label: 'ES', name: tk('Español') },
  { code: 'pl', label: 'PL', name: tk('Polaco') },
  { code: 'it', label: 'IT', name: tk('Italiano') },
  { code: 'pt', label: 'PT', name: tk('Portugués') },
  { code: 'el', label: 'EL', name: tk('Griego') },
];

export const FIELDS: NewsField[] = ['headline', 'title', 'intro'];

/** Largos máximos (los mismos que valida la API en src/pm/news.schemas.ts). */
export const LIMITS = { headline: 80, title: 200, intro: 600, source: 60, url: 500, slug: 80 } as const;

export const emptyTexts = (): NewsTexts =>
  Object.fromEntries(LANGS.map((l) => [l.code, { headline: '', title: '', intro: '' }])) as NewsTexts;

/** Historia: cada hito tiene título y texto por idioma (los mismos largos que valida la API en src/pm/history.schemas.ts). */
export const HISTORY_FIELDS: HistoryField[] = ['title', 'text'];
export const HISTORY_LIMITS = { year: 12, title: 60, text: 400 } as const;
export const emptyHistoryTexts = (): HistoryTexts => Object.fromEntries(LANGS.map((l) => [l.code, { title: '', text: '' }])) as HistoryTexts;

/** Textos por idioma de una noticia o de un hito. */
type LangTexts = Record<NewsLang, Record<string, string>>;
export type LangStateValue = 'full' | 'part' | 'empty';

/** full: todos los textos · part: alguno · empty: ninguno */
export function langState(texts: LangTexts, lang: NewsLang, fields: readonly string[] = FIELDS): LangStateValue {
  const filled = fields.filter((f) => texts[lang]?.[f]?.trim()).length;
  return filled === fields.length ? 'full' : filled ? 'part' : 'empty';
}

/**
 * EN ES PL IT PT EL, en verde lo traducido, en amarillo lo incompleto. Con `stateOf`, quien la usa
 * dice cuándo un idioma está completo (la ficha de una moto no son textos sueltos).
 */
export function LangChips({ texts, fields = FIELDS, stateOf }: { texts?: LangTexts; fields?: readonly string[]; stateOf?: (lang: NewsLang) => LangStateValue }) {
  const { t } = useT();
  return (
    <span className="lang-chips">
      {LANGS.map((l) => {
        const s = stateOf ? stateOf(l.code) : langState(texts!, l.code, fields);
        const what = s === 'full' ? t('completo') : s === 'part' ? t('incompleto') : l.code === 'en' ? t('falta') : t('sale en inglés');
        return (
          <span key={l.code} className={`lang-chip ${s}`} title={`${t(l.name)}: ${what}`}>
            {l.label}
          </span>
        );
      })}
    </span>
  );
}

// En el idioma del dashboard (quien la usa al pintar se vuelve a pintar al cambiarlo: usa useT()).
const dayFmt = perLang((locale) => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }));
const monthFmt = perLang((locale) => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }));

/** "2025-12-08" → "8 dic 2025" · "2026-08" → "agosto de 2026" (en inglés: "8 Dec 2025" · "August 2026") */
export function newsDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m) return date;
  return d ? dayFmt().format(Date.UTC(y, m - 1, d)) : monthFmt().format(Date.UTC(y, m - 1, 1));
}

/** "Capetown 7X meets Pininfarina" → "capetown-7x-meets-pininfarina" */
export const newsSlug = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, LIMITS.slug)
    .replace(/-+$/, '');

/** Tarjeta de noticia del sitio (700 × 437) y la imagen para compartir (1200 × 630). */
export const CARD_RATIO = 700 / 437;
export const OG_RATIO = 1200 / 630;

/** Ancho útil de la foto en la tarjeta (el del recorte 700 × 437), como lo calcula la API. */
export function cardCropWidth(media: Pick<Media, 'width' | 'height'>): number {
  const w = media.width ?? 0;
  const h = media.height ?? 0;
  return Math.min(w, Math.round(h * CARD_RATIO));
}

/** object-position de CSS que reproduce el encuadre del sitio (es la misma cuenta). */
export const objectPosition = (crop: { x: number; y: number }) => `${(crop.x * 100).toFixed(2)}% ${(crop.y * 100).toFixed(2)}%`;

// ------------------------------------------------------------------ gama (Motos)

/** Largos máximos (los mismos que valida la API en src/pm/lineup.schemas.ts). */
export const LINEUP_LIMITS = { familyName: 40, segment: 40, name: 40, euro: 24, tag: 140, figValue: 16, figLabel: 60, eqItem: 120, eqItems: 20 } as const;
/** La ficha tiene cuatro casillas de cifras, como el diseño. */
export const FIGS = 4;

export const emptyFicha = (): FichaTexts => ({ tag: '', figs: Array.from({ length: FIGS }, () => ['', ''] as [string, string]), eq: [] });
export const emptyLineupTexts = (): LineupTexts => Object.fromEntries(LANGS.map((l) => [l.code, emptyFicha()])) as LineupTexts;

/**
 * ¿La ficha de un idioma está completa? En inglés cuentan también los valores de las cifras; en
 * los demás no (suelen ser iguales: «A2», «CVT», y si faltan salen los del inglés). La misma regla
 * que la API (src/pm/lineup.schemas.ts → untranslatedFicha).
 */
export function fichaState(texts: LineupTexts, lang: NewsLang): LangStateValue {
  const f = texts[lang];
  const parts = [!!f.tag.trim(), ...f.figs.map(([v, l]) => !!l.trim() && (lang !== 'en' || !!v.trim())), f.eq.some((e) => e.trim())];
  const filled = parts.filter(Boolean).length;
  return filled === parts.length ? 'full' : filled || f.figs.some(([v]) => v.trim()) ? 'part' : 'empty';
}
